import React, { ReactElement, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  deleteCaasMigrationFacilityMapping,
  setCaasMigrationFacilityMapping,
} from 'api/MigrationReadinessReportAPI';
import { DeleteActionModal } from 'components/DeleteActionModal';
import {
  MigrationMappingSource,
  MigrationReadinessReport,
} from 'types/MigrationReadinessReport';

type Props = {
  report: MigrationReadinessReport
  onChanged: () => Promise<unknown>
}

const parseFacilityId = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^[1-9]\d*$/.test(trimmed)) return null;
  return Number(trimmed);
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { Message?: string, message?: string } } })
    ?.response?.data;
  return data?.Message || data?.message || fallback;
};

const resolveMappingSource = (report: MigrationReadinessReport): string => {
  if (report.MappingSource) return report.MappingSource;
  if (report.V1SyncedFacilityId != null) return MigrationMappingSource.V1Sync;
  return MigrationMappingSource.None;
};

export const CaasMigrationFacilityMappingCard = ({
  report,
  onChanged,
}: Props): ReactElement => {
  const [facilityIdInput, setFacilityIdInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);

  const facilityId = parseFacilityId(facilityIdInput);
  const mappingSource = resolveMappingSource(report);
  const isV1Sync = mappingSource === MigrationMappingSource.V1Sync;
  const isMigration = mappingSource === MigrationMappingSource.Migration;
  const mappedFacilityId = report.MappedFacilityId ?? report.V1SyncedFacilityId;

  const saveMapping = async () => {
    if (facilityId == null) return;

    setIsSaving(true);
    try {
      await setCaasMigrationFacilityMapping(facilityId, { ignoreGlobalCatch: true });
      setFacilityIdInput('');
      toast.success('Facility mapped for CaaS migration.');
      await onChanged();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to save the facility mapping.'));
    } finally {
      setIsSaving(false);
    }
  };

  const removeMapping = async () => {
    setIsRemoving(true);
    try {
      await deleteCaasMigrationFacilityMapping({ ignoreGlobalCatch: true });
      setShowRemoveConfirmation(false);
      toast.success('Migration mapping removed.');
      await onChanged();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to remove the facility mapping.'));
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Header as="h5">CaaS migration facility mapping</Card.Header>
      <Card.Body>
        <p className="text-muted">
          Map this VoiceFriend account to a Go Icon facility so templates can be imported
          without turning on v1 sync.
        </p>

        {isV1Sync && (
          <>
            <p className="font-weight-bold mb-1">V1 synced facility ID</p>
            <p className="mb-2">{mappedFacilityId ?? 'Not set'}</p>
            <p className="mb-0">This account is linked by v1 sync.</p>
          </>
        )}

        {isMigration && (
          <>
            <p className="font-weight-bold mb-1">Mapped facility ID</p>
            <p className="mb-3">{mappedFacilityId ?? 'Not set'}</p>
            <Button
              type="button"
              variant="danger"
              onClick={() => setShowRemoveConfirmation(true)}
            >
              Remove mapping
            </Button>
          </>
        )}

        {!isV1Sync && !isMigration && (
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              saveMapping();
            }}
          >
            <Form.Group controlId="caas-migration-facility-id">
              <Form.Label>Go Icon facility ID</Form.Label>
              <Form.Control
                value={facilityIdInput}
                onChange={(event) => setFacilityIdInput(event.target.value)}
                placeholder="Enter the facility ID"
                inputMode="numeric"
                disabled={isSaving}
              />
              <Form.Text className="text-muted">
                Saving creates a migration mapping for the account you currently have open.
              </Form.Text>
            </Form.Group>
            <Button type="submit" variant="primary" disabled={facilityId == null || isSaving}>
              {isSaving && <Spinner as="span" animation="border" size="sm" className="mr-2" />}
              Save
            </Button>
          </Form>
        )}
      </Card.Body>

      <DeleteActionModal
        isOpen={showRemoveConfirmation}
        title="Remove migration mapping?"
        buttonName="Remove mapping"
        isDeleting={isRemoving}
        onCancel={() => {
          if (!isRemoving) setShowRemoveConfirmation(false);
        }}
        onSuccess={removeMapping}
      >
        <p>
          Removing this mapping stops Import From Legacy for this facility until a facility
          is mapped again. V1 sync is not changed.
        </p>
      </DeleteActionModal>
    </Card>
  );
};
